import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LandingNavbar } from '@/features/landing/components/landing-navbar';
import { TableOfContents } from './table-of-contents';
import type { Heading } from '../utils/extract-headings';

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  headings: Heading[];
  children: ReactNode;
}

export function LegalPageLayout({ title, lastUpdated, headings, children }: LegalPageLayoutProps) {
  return (
    <div className="min-h-screen bg-white">
      <LandingNavbar />

      {/* Spacer for fixed navbar */}
      <div className="h-16" />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page header */}
        <header className="mb-10">
          <h1 className="text-3xl font-bold text-foreground sm:text-4xl">{title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">Ultima actualizacion: {lastUpdated}</p>
        </header>

        {/* Two-column layout */}
        <div className="lg:grid lg:grid-cols-[1fr_16rem] lg:gap-12">
          {/* Main content */}
          <div className="min-w-0">{children}</div>

          {/* Sidebar TOC - hidden on mobile */}
          <aside className="hidden lg:block">
            <TableOfContents headings={headings} />
          </aside>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} Prixia. Todos los derechos reservados.
            </p>
            <nav className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <Link
                to="/terms"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Terminos
              </Link>
              <Link
                to="/privacy"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Privacidad
              </Link>
              <Link
                to="/acceptable-use"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Uso Aceptable
              </Link>
              <Link
                to="/contract"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Contrato SaaS
              </Link>
              <Link
                to="/legal-notice"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Aviso Legal
              </Link>
              <Link
                to="/data-processing"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Procesamiento de Datos
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
